import { KG_LOCATIONS } from './kgLocations.js';
// Every region → district / city / microdistrict a listing can be in.
// `group` drives the section headers in the location pickers; ids are the
// values stored in `district` and the i18n `districts.*` keys. Bishkek is its
// own region (backend enum BISHKEK) with city districts, microdistricts and
// residential massifs. Old listings saved as CHUI + "bishkek" still render —
// the key stays in i18n and mapGeo — it just isn't offered for new ones.
export type DistrictGroup = 'city-district' | 'mkr' | 'massiv' | 'district' | 'town' | 'village' | 'resort';

export interface DistrictDef {
  id: string;
  name: string;
  group: DistrictGroup;
  /** Bishkek microdistricts: the city district they belong to (id). */
  parent?: string;
}

export interface RegionOption {
  id: string;
  name: string;
  value: string;
  districts: DistrictDef[];
}

const BASE_REGIONS: RegionOption[] = [
  {
    id: 'bishkek',
    name: 'Бишкек',
    value: 'BISHKEK',
    districts: [
      { id: 'leninsky', name: 'Ленин району', group: 'city-district' },
      { id: 'oktyabrsky', name: 'Октябрь району', group: 'city-district' },
      { id: 'pervomaisky', name: 'Биринчи Май району', group: 'city-district' },
      { id: 'sverdlovsky', name: 'Свердлов району', group: 'city-district' },
      { id: 'bishkek-center', name: 'Борбор', group: 'mkr' },
      { id: 'golden-square', name: 'Алтын квадрат', group: 'mkr' },
      { id: 'dzhal', name: 'Жал', group: 'mkr' },
      { id: 'verkhniy-dzhal', name: 'Жогорку Жал', group: 'mkr' },
      { id: 'sredniy-dzhal', name: 'Ортоңку Жал', group: 'mkr' },
      { id: 'nizhniy-dzhal', name: 'Төмөнкү Жал', group: 'mkr' },
      { id: 'asanbay', name: 'Асанбай', group: 'mkr' },
      { id: 'vostok-5', name: 'Восток-5', group: 'mkr' },
      { id: 'tunguch', name: 'Туңгуч', group: 'mkr' },
      { id: 'kok-zhar', name: 'Көк-Жар', group: 'mkr' },
      { id: 'magistral', name: 'Магистраль', group: 'mkr' },
      { id: 'ulan', name: 'Улан', group: 'mkr' },
      { id: 'ulan-2', name: 'Улан-2', group: 'mkr' },
      { id: 'uchkun', name: 'Учкун', group: 'mkr' },
      { id: 'yug-2', name: 'Юг-2', group: 'mkr' },
      { id: 'kyzyl-asker', name: 'Кызыл-Аскер', group: 'mkr' },
      { id: 'alamedin-1', name: 'Аламүдүн-1', group: 'mkr' },
      { id: 'pishpek', name: 'Пишпек', group: 'mkr' },
      { id: 'politeh', name: 'Политех', group: 'mkr' },
      { id: 'filarmoniya', name: 'Филармония', group: 'mkr' },
      { id: 'mossovet', name: 'Моссовет', group: 'mkr' },
      { id: 'tsum', name: 'ЦУМ', group: 'mkr' },
      { id: 'osh-bazaar', name: 'Ош базары', group: 'mkr' },
      { id: 'karagachevaya-roshcha', name: 'Карагач токою', group: 'mkr' },
      { id: 'voentorg', name: 'Военторг', group: 'mkr' },
      { id: 'mkr-1', name: '1-кичи район', group: 'mkr' },
      { id: 'mkr-3', name: '3-кичи район', group: 'mkr' },
      { id: 'mkr-4', name: '4-кичи район', group: 'mkr' },
      { id: 'mkr-5', name: '5-кичи район', group: 'mkr' },
      { id: 'mkr-6', name: '6-кичи район', group: 'mkr' },
      { id: 'mkr-7', name: '7-кичи район', group: 'mkr' },
      { id: 'mkr-8', name: '8-кичи район', group: 'mkr' },
      { id: 'mkr-9', name: '9-кичи район', group: 'mkr' },
      { id: 'mkr-10', name: '10-кичи район', group: 'mkr' },
      { id: 'mkr-11', name: '11-кичи район', group: 'mkr' },
      { id: 'mkr-12', name: '12-кичи район', group: 'mkr' },
      { id: 'ak-bata', name: 'Ак-Бата', group: 'massiv' },
      { id: 'ak-bosogo', name: 'Ак-Босого', group: 'massiv' },
      { id: 'ak-zhar', name: 'Ак-Жар', group: 'massiv' },
      { id: 'ak-orgo', name: 'Ак-Өргө', group: 'massiv' },
      { id: 'ak-ordo', name: 'Ак-Ордо', group: 'massiv' },
      { id: 'ala-archa-massiv', name: 'Ала-Арча', group: 'massiv' },
      { id: 'nizhnyaya-ala-archa', name: 'Төмөнкү Ала-Арча', group: 'massiv' },
      { id: 'ala-too-massiv', name: 'Ала-Тоо', group: 'massiv' },
      { id: 'altyn-ordo', name: 'Алтын-Ордо', group: 'massiv' },
      { id: 'archa-beshik', name: 'Арча-Бешик', group: 'massiv' },
      { id: 'bakay-ata-bishkek', name: 'Бакай-Ата', group: 'massiv' },
      { id: 'bugu-ene', name: 'Бугу-Эне', group: 'massiv' },
      { id: 'dordoy', name: 'Дордой', group: 'massiv' },
      { id: 'dostuk', name: 'Достук', group: 'massiv' },
      { id: 'kalys-ordo', name: 'Калыс-Ордо', group: 'massiv' },
      { id: 'kara-zhygach', name: 'Кара-Жыгач', group: 'massiv' },
      { id: 'kasym', name: 'Касым', group: 'massiv' },
      { id: 'kelechek', name: 'Келечек', group: 'massiv' },
      { id: 'kolmo', name: 'Көлмө', group: 'massiv' },
      { id: 'krasny-stroitel', name: 'Кызыл Куруучу', group: 'massiv' },
      { id: 'muras-ordo', name: 'Мурас-Ордо', group: 'massiv' },
      { id: 'orto-say', name: 'Орто-Сай', group: 'massiv' },
      { id: 'ruhiy-muras', name: 'Рухий-Мурас', group: 'massiv' },
      { id: 'tendik', name: 'Теңдик', group: 'massiv' },
      { id: 'tynchtyk', name: 'Тынчтык', group: 'massiv' },
      { id: 'yntymak', name: 'Ынтымак', group: 'massiv' },
      { id: 'chon-aryk', name: 'Чоң-Арык', group: 'massiv' },
      { id: 'ene-say', name: 'Эне-Сай', group: 'massiv' },
    ],
  },
  {
    id: 'chuy',
    name: 'Чүй',
    value: 'CHUI',
    districts: [
      { id: 'alamudun', name: 'Аламүдүн', group: 'district' },
      { id: 'sokuluk', name: 'Сокулук', group: 'district' },
      { id: 'moskovsky', name: 'Москва району', group: 'district' },
      { id: 'panfilov', name: 'Панфилов району', group: 'district' },
      { id: 'jayyl', name: 'Жайыл району', group: 'district' },
      { id: 'kemin', name: 'Кемин району', group: 'district' },
      { id: 'issyk-ata', name: 'Ысык-Ата району', group: 'district' },
      { id: 'chuy-district', name: 'Чүй району', group: 'district' },
      { id: 'tokmok', name: 'Токмок', group: 'town' },
      { id: 'kant', name: 'Кант', group: 'town' },
      { id: 'kara-balta', name: 'Кара-Балта', group: 'town' },
      { id: 'shopokov', name: 'Шопоков', group: 'town' },
      { id: 'belovodskoe', name: 'Беловодское', group: 'town' },
      { id: 'kaindy', name: 'Кайыңды', group: 'town' },
      { id: 'orlovka', name: 'Орловка', group: 'town' },
      { id: 'lebedinovka', name: 'Лебединовка', group: 'town' },
      { id: 'koy-tash', name: 'Кой-Таш', group: 'town' },
      { id: 'novopavlovka', name: 'Новопавловка', group: 'town' },
      { id: 'prigorodnoe', name: 'Пригородное', group: 'town' },
      { id: 'maevka', name: 'Маевка', group: 'town' },
      { id: 'tash-dobo', name: 'Таш-Дөбө', group: 'town' },
    ],
  },
  {
    id: 'osh',
    name: 'Ош',
    value: 'OSH',
    districts: [
      { id: 'osh-city', name: 'Ош', group: 'town' },
      { id: 'kara-suu', name: 'Кара-Суу', group: 'district' },
      { id: 'aravan', name: 'Араван', group: 'district' },
      { id: 'nookat', name: 'Ноокат', group: 'district' },
      { id: 'uzgen', name: 'Өзгөн', group: 'district' },
      { id: 'alai', name: 'Алай', group: 'district' },
      { id: 'chong-alai', name: 'Чоң-Алай', group: 'district' },
      { id: 'kara-kulja', name: 'Кара-Кулжа району', group: 'district' },
    ],
  },
  {
    id: 'jalal-abad',
    name: 'Жалал-Абад',
    value: 'JALAL_ABAD',
    districts: [
      { id: 'jalalabad-city', name: 'Жалал-Абад', group: 'town' },
      { id: 'tash-kumyr', name: 'Таш-Көмүр', group: 'town' },
      { id: 'kara-kul', name: 'Кара-Көл', group: 'town' },
      { id: 'mailuu-suu', name: 'Майлуу-Суу', group: 'town' },
      { id: 'kochkor-ata', name: 'Кочкор-Ата', group: 'town' },
      { id: 'kerben', name: 'Кербен', group: 'town' },
      { id: 'suzak', name: 'Сузак', group: 'district' },
      { id: 'bazarkorgon', name: 'Базар-Коргон', group: 'district' },
      { id: 'nooken', name: 'Ноокен', group: 'district' },
      { id: 'aksiy', name: 'Аксы', group: 'district' },
      { id: 'ala-buka', name: 'Ала-Бука', group: 'district' },
      { id: 'chatkal', name: 'Чаткал', group: 'district' },
      { id: 'toguz-toro', name: 'Тогуз-Торо', group: 'district' },
      { id: 'toktogul', name: 'Токтогул', group: 'district' },
    ],
  },
  {
    id: 'yssyk_kol',
    name: 'Иссык-Куль',
    value: 'YSSYK_KOL',
    districts: [
      { id: 'karakol', name: 'Каракол', group: 'town' },
      { id: 'cholpon-ata', name: 'Чолпон-Ата', group: 'town' },
      { id: 'balykchy', name: 'Балыкчы', group: 'town' },
      { id: 'issyk-kul-district', name: 'Ысык-Көл району', group: 'district' },
      { id: 'tyup', name: 'Түп', group: 'district' },
      { id: 'ak-suu', name: 'Ак-Суу', group: 'district' },
      { id: 'tong', name: 'Тоң', group: 'district' },
      { id: 'jethy-oguz', name: 'Жети-Өгүз', group: 'district' },
      { id: 'bosteri', name: 'Бостери', group: 'resort' },
      { id: 'tamchy', name: 'Тамчы', group: 'resort' },
      { id: 'kara-oy', name: 'Кара-Ой', group: 'resort' },
      { id: 'sary-oy', name: 'Сары-Ой', group: 'resort' },
      { id: 'chon-sary-oy', name: 'Чоң-Сары-Ой', group: 'resort' },
      { id: 'chok-tal', name: 'Чок-Тал', group: 'resort' },
      { id: 'kosh-kol', name: 'Кош-Көл', group: 'resort' },
      { id: 'baktuu-dolonotu', name: 'Бактуу-Долоноту', group: 'resort' },
      { id: 'bokonbaevo', name: 'Бөкөнбаев', group: 'resort' },
    ],
  },
  {
    id: 'naryn',
    name: 'Нарын',
    value: 'NARYN',
    districts: [
      { id: 'naryn-city', name: 'Нарын', group: 'town' },
      { id: 'naryn-district', name: 'Нарын району', group: 'district' },
      { id: 'at-bashy', name: 'Ат-Башы', group: 'district' },
      { id: 'kochkor', name: 'Кочкор', group: 'district' },
      { id: 'jumgal', name: 'Жумгал', group: 'district' },
      { id: 'ak-talaa', name: 'Ак-Талаа', group: 'district' },
    ],
  },
  {
    id: 'talas',
    name: 'Талас',
    value: 'TALAS',
    districts: [
      { id: 'talas-city', name: 'Талас', group: 'town' },
      { id: 'talas-district', name: 'Талас району', group: 'district' },
      { id: 'kara-buura', name: 'Кара-Буура', group: 'district' },
      { id: 'manas', name: 'Манас', group: 'district' },
      { id: 'bakai-ata', name: 'Бакай-Ата', group: 'district' },
    ],
  },
  {
    id: 'batken',
    name: 'Баткен',
    value: 'BATKEN',
    districts: [
      { id: 'batken-city', name: 'Баткен', group: 'town' },
      { id: 'kyzyl-kiya', name: 'Кызыл-Кыя', group: 'town' },
      { id: 'sulukta', name: 'Сүлүктү', group: 'town' },
      { id: 'isfana', name: 'Исфана', group: 'town' },
      { id: 'batken-district', name: 'Баткен району', group: 'district' },
      { id: 'leilek', name: 'Лейлек', group: 'district' },
      { id: 'kadamdzhai', name: 'Кадамжай', group: 'district' },
    ],
  },
];

// Full settlement list (scripts/build-kg-locations.mjs) merged over the
// hand-made list above: generated entries first (they carry coordinates and
// the Bishkek city district), then any older id not in the file — listings
// saved with those ids keep resolving.
export const REGIONS: RegionOption[] = BASE_REGIONS.map((r) => {
  const generated = (KG_LOCATIONS[r.value] ?? []) as DistrictDef[];
  const ids = new Set(generated.map((d) => d.id));
  const collator = new Intl.Collator('ru', { numeric: true });
  const merged = [...generated, ...r.districts.filter((d) => !ids.has(d.id))];
  merged.sort((a, b) => (a.group === 'city-district' && b.group === 'city-district' ? 0 : collator.compare(a.name, b.name)));
  return { ...r, districts: merged };
});

/** Russian names of every district id — registered as i18n fallbacks (see src/i18n/index.ts). */
export const DISTRICT_NAMES: Record<string, string> = Object.fromEntries(
  Object.values(KG_LOCATIONS).flat().map((d) => [d.id, d.name]),
);

const GROUP_ORDER: DistrictGroup[] = ['city-district', 'mkr', 'massiv', 'town', 'district', 'village', 'resort'];

/** A region's districts split into ordered sections, for <optgroup>s and pickers. */
export function groupDistricts(region: string | undefined) {
  const districts = REGIONS.find((r) => r.value === region)?.districts ?? [];
  return GROUP_ORDER.map((group) => ({ group, items: districts.filter((d) => d.group === group) })).filter(
    (g) => g.items.length > 0,
  );
}

export const CATEGORIES = [
  {
    id: 'apartments',
    name: 'Apartments',
    icon: 'Building2',
    value: 'APARTMENT',
  },
  { id: 'houses', name: 'Houses', icon: 'Home', value: 'HOUSE' },
  { id: 'plots', name: 'Plots', icon: 'Map', value: 'PLOT' },
  { id: 'commercial', name: 'Commercial', icon: 'Store', value: 'COMMERCIAL' },
];
